import { clearToken, getToken } from '../auth/session';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message ?? code);
  }
}

const base = import.meta.env.VITE_API_BASE ?? '';

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${base}${path}`, { ...init, headers });
  const text = await res.text();
  let json: Record<string, unknown> = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    /* non-json */
  }

  if (res.status === 401) {
    clearToken();
    window.location.assign('/connect');
    throw new ApiError(401, 'unauthorized');
  }

  if (!res.ok) {
    const code =
      typeof json.error === 'string' && json.error
        ? json.error
        : res.status === 404
          ? 'not_found'
          : 'request_failed';
    throw new ApiError(res.status, code, text);
  }

  return json as T;
}

export function userMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const map: Record<string, string> = {
      name_required: 'Please enter a name.',
      invalid_name: "Use letters, numbers, spaces, or ' - . (max 20).",
      name_taken: 'That name is already in use.',
      members_full: 'Maximum family members reached.',
      member_has_todos: 'Remove reminders for this member on your Remindo first.',
      web_only: 'This action is only available in the web app.',
      too_large: 'Photo is too large (max 5 MB).',
      unsupported_type: 'Use JPEG, PNG, or WebP.',
      phones_full: 'This family already has 4 phones connected.',
      invalid_pair_token: 'This code expired. Scan again on your Remindo.',
      voice_pack_required: 'Pick a voice to preview.',
      member_not_found: 'That person was already removed.',
      tts_failed: 'Voice preview failed. Try again in a moment.',
      not_found: 'This feature needs a server update. Restart Remindo-server and try again.',
      request_failed: 'Request failed. If you just updated code, restart the local server.',
    };
    return map[err.code] ?? `Something went wrong (${err.code})`;
  }
  return 'Network error. Try again.';
}
